import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo, useRef } from 'react';
import { User } from 'firebase/auth';
import { subscriptionService } from '../services/subscriptionService';

export type SubscriptionStatus = {
  plano: string | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  ativo?: boolean;
  validade?: string;
};

interface SubscriptionStatusProviderProps {
  user: User | null;
  children: ReactNode;
}

export const SubscriptionStatusContext = createContext<{
  status: SubscriptionStatus;
  refresh: () => void;
  setLocalStatus: (s: SubscriptionStatus) => void;
} | undefined>(undefined);

export const SubscriptionStatusProvider = ({ user, children }: SubscriptionStatusProviderProps) => {
  const [status, setStatus] = useState<SubscriptionStatus>({ plano: null, token: null, loading: true, error: null });
  const [refreshCount, setRefreshCount] = useState(0);
  const isLoadingRef = useRef(false);

  const refresh = useCallback(() => setRefreshCount(c => c + 1), []);

  useEffect(() => {
    // Se não há usuário, reseta status
    if (!user) {
      setStatus({ plano: null, token: null, loading: false, error: null });
      isLoadingRef.current = false;
      return;
    }

    // Evita requisições duplicadas
    if (isLoadingRef.current) {
      return;
    }

    isLoadingRef.current = true;
    setStatus(s => ({ ...s, loading: true }));

    console.log('[DEBUG] SubscriptionStatusProvider - Buscando assinatura para:', user.uid);

    // Usa o novo serviço unificado (Firestore em produção, API em desenvolvimento)
    subscriptionService.hasSubscription(user.uid)
      .then(res => {
        const { hasSubscription, plano, token } = res;

        // Processa os dados
        const processedPlano = (hasSubscription && plano && plano.trim() !== '') ? plano : null;
        const processedToken = (hasSubscription && token && token.trim() !== '') ? token : null;

        console.log('[DEBUG] SubscriptionStatusProvider - Resposta:', { hasSubscription, plano: processedPlano });

        setStatus({
          plano: processedPlano,
          token: processedToken,
          loading: false,
          error: null
        });
        isLoadingRef.current = false;
      })
      .catch(err => {
        console.error('[ERROR] SubscriptionStatusProvider:', err.message);
        setStatus({ plano: null, token: null, loading: false, error: err.message });
        isLoadingRef.current = false;
      });
  }, [user, refreshCount]);

  const setLocalStatus = useCallback((s: SubscriptionStatus) => {
    setStatus(s);
  }, []);

  const value = useMemo(() => ({ status, refresh, setLocalStatus }), [status, refresh, setLocalStatus]);

  return (
    <SubscriptionStatusContext.Provider value={value}>
      {children}
    </SubscriptionStatusContext.Provider>
  );
};

export const useSubscriptionStatusContext = () => {
  const ctx = useContext(SubscriptionStatusContext);
  if (!ctx) throw new Error('useSubscriptionStatusContext must be used within SubscriptionStatusProvider');
  return ctx;
};
